// ClarificationCard — shown after a clarification_requested SSE event when
// a sub-agent calls ask_user. The user types or picks an answer; submission
// POSTs to /chat/sessions/{id}/clarify and re-opens the SSE stream.

import { useCallback, useState } from "react";

import { API_BASE, readCachedAccessToken } from "@/services/api/client";
import type { ChatStreamEvent } from "@/services/chat/streamService";
import { cn } from "@/utils/cn";

export interface ClarificationPayload {
  question: string;
  options?: string[] | null;
  context?: string | null;
  asking_sub_agent: string;
  trace_id: string | null;
}

export interface ClarificationCardProps {
  payload: ClarificationPayload;
  sessionId: string;
  getAccessToken?: () => Promise<string | null>;
  onSubmit: (stream: AsyncGenerator<ChatStreamEvent>) => void;
  onCancel: () => void;
}

const SUB_AGENT_LABELS: Record<string, string> = {
  regulatory: "Regulatory",
  vulnerability: "Vulnerability",
  legal_review: "Legal Review",
  vendor_risk: "Vendor Risk",
  framework_crosswalk: "Framework Crosswalk",
  incident_response: "Incident Response",
  trust_eidas: "Trust & eIDAS",
  aml_fraud: "AML & Fraud",
  pen_test: "Pen Test",
  detection_engineering: "Detection Engineering",
  cloud_security: "Cloud Security",
  ai_security: "AI Security",
  identity_crypto: "Identity & Crypto",
  data_governance: "Data Governance",
};

async function* clarifyStream(
  sessionId: string,
  answer: string,
  getAccessToken?: () => Promise<string | null>,
): AsyncGenerator<ChatStreamEvent> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  let token: string | null = null;
  if (getAccessToken) {
    try {
      token = await getAccessToken();
    } catch {
      token = null;
    }
  }
  if (!token) token = readCachedAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${API_BASE}/chat/sessions/${sessionId}/clarify`, {
    method: "POST",
    headers,
    body: JSON.stringify({ answer }),
  });

  if (!response.ok || !response.body) {
    throw new Error(`Clarify failed: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf("\n\n");
      while (boundary !== -1) {
        const raw = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const parsed = parseBlock(raw);
        if (parsed) yield parsed;
        boundary = buffer.indexOf("\n\n");
      }
    }
    if (buffer.trim().length) {
      const parsed = parseBlock(buffer);
      if (parsed) yield parsed;
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // ignore
    }
  }
}

function parseBlock(raw: string): ChatStreamEvent | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of raw.split("\n")) {
    if (line.startsWith("event: ")) event = line.slice(7).trim();
    else if (line.startsWith("data: ")) dataLines.push(line.slice(6));
  }
  if (!event || event === "message") return null;
  let payloadParsed: unknown = dataLines.join("\n");
  try {
    payloadParsed = JSON.parse(dataLines.join("\n"));
  } catch {
    // pass through
  }
  return { kind: event as ChatStreamEvent["kind"], data: payloadParsed } as ChatStreamEvent;
}

export function ClarificationCard({
  payload,
  sessionId,
  getAccessToken,
  onSubmit,
  onCancel,
}: ClarificationCardProps) {
  const hasOptions = Boolean(payload.options && payload.options.length > 0);
  const [answer, setAnswer] = useState<string>(hasOptions ? "" : "");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (!answer.trim()) return;
    setSubmitting(true);
    try {
      const stream = clarifyStream(sessionId, answer.trim(), getAccessToken);
      onSubmit(stream);
    } catch {
      setSubmitting(false);
    }
  }, [answer, getAccessToken, onSubmit, sessionId]);

  const subAgentLabel =
    SUB_AGENT_LABELS[payload.asking_sub_agent] ?? payload.asking_sub_agent;

  return (
    <div className="animate-ery-rise space-y-3 rounded-card border border-oxblood-300 bg-oxblood-50 p-4 text-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="label-mono">{subAgentLabel} sub-agent asks</p>
          <p className="mt-1.5 font-serif text-base leading-snug text-espresso-700">
            {payload.question}
          </p>
          {payload.context && (
            <p className="mt-1 font-serif text-xs leading-relaxed text-warmgray-500">
              {payload.context}
            </p>
          )}
        </div>
        <button
          onClick={onCancel}
          className="shrink-0 font-mono text-xs text-oxblood-400 underline underline-offset-2 transition-colors duration-200 ease-atelier hover:text-oxblood-600"
          disabled={submitting}
        >
          Cancel
        </button>
      </div>

      {hasOptions ? (
        <div className="space-y-1.5">
          {payload.options!.map((opt) => (
            <label
              key={opt}
              className={cn(
                "flex cursor-pointer items-center gap-2 rounded-panel border p-2",
                "transition-colors duration-200 ease-atelier",
                "border-oxblood-200 bg-parchment-50 hover:bg-oxblood-50",
                answer === opt && "border-oxblood-500 ring-2 ring-oxblood-200",
              )}
            >
              <input
                type="radio"
                name="clarify_answer"
                value={opt}
                checked={answer === opt}
                onChange={() => setAnswer(opt)}
                className="accent-oxblood-500"
              />
              <span className="font-serif text-espresso-700">{opt}</span>
            </label>
          ))}
        </div>
      ) : (
        <textarea
          className="w-full resize-none rounded-panel border border-oxblood-200 bg-parchment-50 p-2 font-serif text-sm text-espresso-700 placeholder:text-warmgray-300 focus:border-oxblood-400 focus:outline-none focus:ring-2 focus:ring-oxblood-200"
          rows={3}
          placeholder="Type your answer…"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          autoFocus
          disabled={submitting}
        />
      )}

      <div className="flex gap-2 pt-1">
        <button
          onClick={handleSubmit}
          disabled={submitting || !answer.trim()}
          className="btn-oxblood h-[34px] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-none"
        >
          {submitting ? "Sending…" : "Send answer"}
        </button>
        <button
          onClick={onCancel}
          disabled={submitting}
          className="btn-outline h-[34px] disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
